<template lang="pug">
modal#delete-account-confirmation-code-modal.modal.fade(
  :close-button-label='$t("modal.deleteAccountConfirmationCode.closeButtonLabel")'
  :disable-process-button='disableProcessButton'
  :loading='loading'
  :process-button-label='$t("modal.deleteAccountConfirmationCode.processButtonLabel")'
  :remove-close-button='true'
  :title='$t("modal.deleteAccountConfirmationCode.title")'
  @closed='onClose'
  @opened='onOpen'
  @process='submit'
  data-bs-backdrop='static'
  data-bs-keyboard='false'
  processButtonClass='btn-primary'
  ref='modal'
  size='lg'
)
  .text-center
    div
      icon.delete-account-confirmation-code__icon(name='envelope-circle-check')
      p.text-center.mb-0.mt-4(
        v-html='$t("modal.deleteAccountConfirmationCode.message", { email: email })'
      )
      p.text-center.mb-0 {{ $t('modal.deleteAccountConfirmationCode.exclusionMessage') }}
      ValidationProvider(
        name='code'
        rules='required|code'
        v-slot='{ errors }'
      )
        input.form-control(
          :class='{ "is-complete": isCodeComplete }'
          :value='code'
          @input='onCodeInput'
          autocomplete='off'
          inputmode='numeric'
          maxlength='6'
          pattern='[0-9]{6}'
          required
          type='text'
        )
      p.delete-account-confirmation-code__error.text-center.mb-2.mt-1(
        v-if='hasInvalidCharacters'
      ) {{ $t('validations.code') }}
      p.delete-account-confirmation-code__meta.text-center.mb-4.mt-1(
        v-html='expirationTimeLabel'
        v-if='hasValidExpireAt'
      )
      .delete-account-confirmation-code__row
        p.text-center.mb-0(:hidden='!isExpired') {{ $t('modal.deleteAccountConfirmationCode.resendMessage') }}
        button.delete-account-confirmation-code__link(
          :disabled='isResending'
          :hidden='!isExpired'
          @click='resendCode'
          type='button'
        ) {{ $t('modal.deleteAccountConfirmationCode.retryCode') }}
</template>

<script>
import Icon from '@/ui/atoms/Icon.vue';
import Modal from '@/ui/molecules/Modal.vue';
import ImageLoader from '@/ui/molecules/ImageLoader.vue';
import rules from '@/utils/ValidateRules';

export default {
  name: 'DeleteAccountConfirmationCodeModal',
  components: {
    Modal,
    Icon,
    ImageLoader,
  },
  props: {
    expireAt: {
      type: String,
      required: true,
    },
    isResending: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      code: '',
      now: new Date(),
      timerId: null,
      isOpen: false,
      rawCode: '',
    };
  },
  beforeDestroy() {
    this.stopTimer();
  },
  computed: {
    email() {
      return this.$root.user.email;
    },
    hasValidExpireAt() {
      return (
        Boolean(this.expireAt) &&
        !Number.isNaN(new Date(this.expireAt).getTime())
      );
    },
    expiresAtMs() {
      if (!this.hasValidExpireAt) {
        return null;
      }
      return new Date(this.expireAt).getTime();
    },
    remainingTime() {
      if (!this.expiresAtMs) {
        return 0;
      }
      return Math.max(this.expiresAtMs - this.now, 0);
    },
    expirationMinutes() {
      const totalSeconds = Math.floor(this.remainingTime / 1000);
      const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
      const seconds = String(totalSeconds % 60).padStart(2, '0');
      return `${minutes}:${seconds}`;
    },
    isExpired() {
      return this.remainingTime === 0;
    },
    isCodeComplete() {
      return /^\d{6}$/.test(this.code);
    },
    disableProcessButton() {
      if (this.loading) {
        return true;
      }
      if (this.isExpired || !this.hasValidExpireAt || this.isResending) {
        return true;
      }
      return !this.code || this.code.length !== 6 || !this.isCodeComplete;
    },
    expirationTimeLabel() {
      if (this.isExpired) {
        return this.$t('modal.deleteAccountConfirmationCode.expiredCodeLabel');
      }
      return this.$t(
        'modal.deleteAccountConfirmationCode.expirationCodeLabel',
        {
          expirationMinutesTime: this.expirationMinutes,
        }
      );
    },
    hasInvalidCharacters() {
      return this.rawCode !== this.code;
    },
  },
  watch: {
    expireAt: {
      immediate: true,
      handler() {
        this.now = new Date();
        if (this.isOpen && this.hasValidExpireAt) {
          this.startTimer();
        }
      },
    },
  },
  methods: {
    onCodeInput(event) {
      this.rawCode = event.target.value;
      this.code = rules.stripNonDigits(event.target.value).slice(0, 6);
    },
    submit() {
      this.$emit('process', this.code);
    },
    startTimer() {
      if (!this.hasValidExpireAt) {
        return;
      }
      this.now = new Date();
      this.stopTimer();
      this.timerId = setInterval(() => {
        this.now = new Date();
        if (this.isExpired) {
          this.stopTimer();
        }
      }, 1000);
    },
    stopTimer() {
      if (this.timerId) {
        clearInterval(this.timerId);
        this.timerId = null;
      }
    },
    resendCode() {
      if (this.isResending) {
        return;
      }
      this.code = '';
      this.$emit('resend-code');
    },
    onClose() {
      this.isOpen = false;
      this.stopTimer();
    },
    onOpen() {
      this.code = '';
      this.rawCode = '';
      this.isOpen = true;
      this.startTimer();
    },
  },
};
</script>
<style scoped>
.delete-account-confirmation-code__meta {
  font-size: 14px;
  color: #cccccc;
}

.delete-account-confirmation-code__error {
  font-size: 12px;
  color: #d00a00;
}

.delete-account-confirmation-code__row {
  margin-left: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 14px;
  vertical-align: center;
  gap: 8px;
}

.delete-account-confirmation-code__row p,
.delete-account-confirmation-code__row a {
  margin: 0;
}

.delete-account-confirmation-code__link {
  color: #013d52;
  font-weight: bold;
}

.delete-account-confirmation-code__meta {
  font-size: 14px;
  color: #555;
}

.delete-account-confirmation-code__row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  margin-top: 4px;
}

.delete-account-confirmation-code__row p,
.delete-account-confirmation-code__row a {
  margin: 0;
}

.delete-account-confirmation-code__link {
  color: #013d52;
  font-weight: 700;
  text-decoration: none;
}

.delete-account-confirmation-code__link:hover {
  text-decoration: underline;
}

.delete-account-confirmation-code__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin: 0 auto 12px;
  border-radius: 50%;
  background: #eef3f5;
  color: #013d52;
  font-size: 28px;
  line-height: 1;
}

input.form-control {
  --slot-size: 44px;
  --slot-gap: 10px;
  --slots: 6;
  --track: 26px;
  --slots-width: calc(
    (var(--slot-size) * var(--slots)) + (var(--slot-gap) * (var(--slots) - 1))
  );

  display: block;
  width: 100%;
  max-width: 360px;
  margin: 16px auto 0;
  padding: 0 0 0 12px;

  height: 52px;
  box-sizing: border-box;
  border: 1px solid #cfd8dc;
  border-radius: 8px;
  outline: none;

  font-family: monospace;
  font-size: 28px;
  font-weight: 700;
  line-height: 52px;
  text-align: center;
  letter-spacing: var(--track);
  color: #013d52;
  caret-color: #013d52;

  background-color: transparent;
  background-image: linear-gradient(#eef3f5, #eef3f5),
    repeating-linear-gradient(
      to right,
      #eef3f5 0,
      #eef3f5 var(--slot-size),
      transparent var(--slot-size),
      transparent calc(var(--slot-size) + var(--slot-gap))
    );
  background-repeat: no-repeat, repeat-x;
  background-size: var(--slots-width) 100%;
  background-position: center;
}

input.form-control:focus {
  border-color: #013d52;
  box-shadow: 0 0 0 2px rgba(1, 61, 82, 0.12);
}

input.form-control.is-complete {
  caret-color: transparent;
}
</style>
